import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { CalendarX, DotsThreeVertical, TrashSimple } from "@phosphor-icons/react"
import { ConnectionHandler } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button.tsx"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu.tsx"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { getTakenChatRoute } from "@/modules/chats/lib/routing-helpers"
import { AgendaAction, AgendaEntryStatus } from "@/modules/patients/agenda"
import { AgendaAppointmentDrawer } from "@/modules/patients/agenda/teleconsultation/agenda-appointment-drawer/agenda-appointment-drawer"
import { BillingDialog } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/billing-dialog"
import { OngoingAppointmentActionMenu } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/ongoing-appointment-action-menu"
import { PastAppointmentActionMenu } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/past-appointment-action-menu"
import { PlannedAppointmentActionMenu } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/planned-appointment-action-menu"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"
import { usePatientEpisodesOfCareData } from "@/modules/patients/hooks/usePatientEpisodesOfCareData"
import { useProgressNoteData } from "@/modules/patients/hooks/useProgressNoteData"
import { AppointmentAgenda } from "@/modules/patients/libs/categorize-date-ranges"
import { useCancelAppointmentMutation } from "@/modules/patients/services/useCancelAppointmentMutation"
import { useDeleteAppointmentMutation } from "@/modules/patients/services/useDeleteAppointmentMutation"
import { useRequestTakeOverPatientMutation } from "@/modules/patients/services/useRequestTakeOverPatientMutation"
import { ConfirmationPropsWithoutOpen, useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface AppointmentActionMenuProps {
  onHandleRowClick: (appointment: AppointmentAgenda, mode: AgendaAction) => void
  rowData: AppointmentAgenda
  agendaEntryStatus: AgendaEntryStatus
}

const AppointmentActionMenu = (props: AppointmentActionMenuProps) => {
  const { onHandleRowClick, rowData, agendaEntryStatus } = props
  const { patient, id: appointmentId } = rowData
  const { t } = useTranslation()
  const { isEditMode, account } = useOutletContext<{
    isEditMode: boolean
    account: NonNullable<PatientData>["account"]
  }>()
  const user = useUser()

  const [requestTakeOverPatient] = useRequestTakeOverPatientMutation()
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const patientEpisodesOfCareData = usePatientEpisodesOfCareData()
  const progressNoteData = useProgressNoteData(patientEpisodesOfCareData, isEditMode)
  const deleteAppointmentMutation = useDeleteAppointmentMutation()
  const cancelAppointmentMutation = useCancelAppointmentMutation()
  const openDrawer = useDrawerStore((state) => state.openDrawer)

  const [isBillingDialogOpenWithEdit, setIsBillingDialogOpenWithEdit] = useState<{
    isOpen: boolean
    isEditable: boolean
  } | null>(null)

  const handleCancelAppointment = () => {
    if (!rowData.patient || !rowData.appointment) return

    const { appointment } = rowData

    cancelAppointmentMutation({ id: appointment.id, willRelease: false, shouldIncreaseMissedCall: false })

    cancel()
  }

  const handleDeleteAppointment = () => {
    if (!rowData.patient || !rowData.appointment) return

    const { appointment, patient } = rowData
    const connectionId = ConnectionHandler.getConnectionID(patient.id, "patientIdConnection_appointments")

    deleteAppointmentMutation({ id: appointment.id }, [connectionId], appointment.followUpForProgressNote?.id)

    cancel()
  }

  const getCancelConfirmationContent = (account: NonNullable<PatientData>["account"]): ConfirmationPropsWithoutOpen => {
    const content = account
      ? t("patient.closeAppointmentDialog.appUserCancelConfirm")
      : t("patient.closeAppointmentDialog.nonAppUserCancelConfirm")

    return {
      title: t("patient.closeAppointmentDialog.cancelTeleconsultation"),
      content,
      confirmVariant: "danger",
      confirmText: t("patient.closeAppointmentDialog.cancelTeleconsultation"),
      confirmIcon: CalendarX,
      cancelText: t("common.button.keep"),
      onConfirm: () => handleCancelAppointment(),
    }
  }

  const getDeleteConfirmationContent = (account: NonNullable<PatientData>["account"]): ConfirmationPropsWithoutOpen => {
    const content = account
      ? t("patient.closeAppointmentDialog.appUserDeleteConfirm")
      : t("patient.closeAppointmentDialog.nonAppUserDeleteConfirm")

    return {
      title: t("patient.closeAppointmentDialog.deleteTeleconsultation"),
      content,
      confirmVariant: "danger",
      confirmText: t("patient.closeAppointmentDialog.deleteTeleconsultation"),
      confirmIcon: TrashSimple,
      cancelText: t("common.button.keep"),
      onConfirm: () => handleDeleteAppointment(),
    }
  }

  const handleCancelAction = (event: React.MouseEvent<HTMLDivElement>) => {
    const { appointment, patient } = rowData

    if (!patient || !appointment) return
    event.stopPropagation()

    confirm(getCancelConfirmationContent(account))
  }

  const handleDeleteAction = (event: React.MouseEvent<HTMLDivElement>) => {
    const { appointment, patient } = rowData

    if (!patient || !appointment) return
    event.stopPropagation()

    const appointmentHasProgressNote = progressNoteData.progressNoteAndParentData.some(
      (p) => p.episodeOfCareProgressNoteData.appointment.id === appointment.id,
    )

    if (appointmentHasProgressNote) {
      confirm({
        title: t("patient.closeAppointmentDialog.deleteNotPossible"),
        content: t("patient.closeAppointmentDialog.deleteNotPossibleDescription"),
        cancelText: t("common.button.close"),
        isShowConfirmButton: false,
      })
    } else {
      confirm(getDeleteConfirmationContent(account))
    }
  }

  const handleOpenAgendaDrawer = (rowData: AppointmentAgenda) => {
    openDrawer(() => <AgendaAppointmentDrawer selectedAppointmentId={rowData.id} />, "AgendaAppointmentDrawer")
  }

  const handleTakeConsultation = () => {
    if (patient) {
      const enableConfirmBeforeLeaving = () => {
        window.disableConfirmBeforeLeaving = false
      }

      window.disableConfirmBeforeLeaving = true
      requestTakeOverPatient(
        { patientId: patient.id, appointmentId },
        (response) => {
          const chatThreadId =
            response.requestTakeOverPatientFileLock.patientFileLockOutput?.inContextOfAppointment?.chatDetails
              ?.chatThreadId

          if (chatThreadId) {
            window.location.href = getTakenChatRoute(chatThreadId)
          } else {
            window.location.href = `/patients/${patient?.country}/${patient.id}?appointmentId=${appointmentId}`
          }
        },
        enableConfirmBeforeLeaving,
        enableConfirmBeforeLeaving,
      )
    }
  }

  const handleEdit = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    onHandleRowClick(rowData, "EDIT")
    e.stopPropagation()
  }

  const handleShowBillingInformation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setIsBillingDialogOpenWithEdit({ isOpen: true, isEditable: false })
    FaroInsightsWrapper.pushEvent("agenda_billing_information_view")
    e.stopPropagation()
  }

  const handleEditBillingInformation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setIsBillingDialogOpenWithEdit({ isOpen: true, isEditable: true })
    FaroInsightsWrapper.pushEvent("agenda_billing_information_edit")
    e.stopPropagation()
  }

  const renderMenuItem = () => {
    switch (agendaEntryStatus) {
      case "PLANNED":
        return (
          <PlannedAppointmentActionMenu
            isEditMode={isEditMode}
            rowData={rowData}
            user={user}
            onClickEdit={handleEdit}
            onClickOpenDrawer={() => {
              handleOpenAgendaDrawer(rowData)
            }}
            onClickCancelTeleconsultation={handleCancelAction}
            onClickDeleteTeleconsultation={handleDeleteAction}
            onClickTakeConsultation={handleTakeConsultation}
          />
        )
      case "ONGOING":
        return (
          <OngoingAppointmentActionMenu
            isEditMode={isEditMode}
            onClickEdit={handleEdit}
            onClickOpenDrawer={() => {
              handleOpenAgendaDrawer(rowData)
            }}
          />
        )
      case "PAST":
        return (
          <PastAppointmentActionMenu
            isEditMode={isEditMode}
            rowData={rowData}
            user={user}
            onClickOpenDrawer={() => {
              handleOpenAgendaDrawer(rowData)
            }}
            onClickShowBillingInformation={handleShowBillingInformation}
            onClickEditBillingInformation={handleEditBillingInformation}
          />
        )
    }
  }

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild className="outline-hidden">
          <Button
            data-testid="action-button"
            variant="circle"
            className="action-button absolute top-0.5 right-0.75 h-9 w-9 p-0 outline-hidden!"
            onClick={(event) => event.stopPropagation()}>
            <DotsThreeVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          hideWhenDetached
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          align="end"
          data-testid="agenda-options-content"
          className="min-w-63 rounded-lg border-none p-2 leading-5 transition-all duration-200">
          {renderMenuItem()}
        </DropdownMenuContent>
      </DropdownMenu>
      {isBillingDialogOpenWithEdit?.isOpen && (
        <BillingDialog
          appointmentId={rowData.id}
          onClose={() => {
            setIsBillingDialogOpenWithEdit(null)
          }}
          isEditable={isBillingDialogOpenWithEdit.isEditable}
        />
      )}
    </>
  )
}

export { AppointmentActionMenu }
